/**
 * Category Form Drawer Component
 * Create/Edit Category form in a drawer
 */

import { useEffect } from 'react'
import { useTranslation, useForm, zodResolver, z } from '@spcc/core'
import {
  BaseDrawer,
  Button,
  Input,
  Label,
  Switch,
} from '@spcc/uikit'
import { Tag, Save } from 'lucide-react'
import type {
  Category,
  CreateCategoryRequest,
  UpdateCategoryRequest,
} from '../../domain/types'

// ============================================================================
// Form Schema
// ============================================================================

const categoryFormSchema = z.object({
  name: z.object({
    en: z.string().min(1, 'Name is required'),
    ar: z.string().optional(),
  }),
  description: z
    .object({
      en: z.string().optional(),
      ar: z.string().optional(),
    })
    .optional(),
  code: z.string().optional(),
  color: z.string().optional(),
  isActive: z.boolean(),
})

type CategoryFormData = z.infer<typeof categoryFormSchema>

// ============================================================================
// Helper Functions
// ============================================================================

function categoryToFormData(category: Category | null): CategoryFormData {
  if (!category) {
    return {
      name: { en: '', ar: '' },
      description: { en: '', ar: '' },
      code: '',
      color: '',
      isActive: true,
    }
  }
  return {
    name: {
      en: category.name.en,
      ar: category.name.ar ?? '',
    },
    description: {
      en: category.description?.en ?? '',
      ar: category.description?.ar ?? '',
    },
    code: category.code ?? '',
    color: category.color ?? '',
    isActive: category.isActive,
  }
}

function buildSubmitPayload(
  data: CategoryFormData,
  category: Category | null
): CreateCategoryRequest | UpdateCategoryRequest {
  const base: CreateCategoryRequest = {
    name: {
      en: data.name.en.trim(),
      ...(data.name.ar?.trim() ? { ar: data.name.ar.trim() } : {}),
    },
    description:
      data.description?.en?.trim() || data.description?.ar?.trim()
        ? {
            en: data.description?.en?.trim() ?? '',
            ...(data.description?.ar?.trim() ? { ar: data.description.ar.trim() } : {}),
          }
        : undefined,
    code: data.code || undefined,
    color: data.color || undefined,
    isActive: data.isActive,
  }
  if (category) {
    return { ...base, documentId: category.documentId } as UpdateCategoryRequest
  }
  return base
}

// ============================================================================
// Component Props
// ============================================================================

interface CategoryFormDrawerProps {
  isOpen: boolean
  onClose: () => void
  category: Category | null
  onSubmit: (data: CreateCategoryRequest | UpdateCategoryRequest) => void
  isSaving: boolean
}

// ============================================================================
// Component
// ============================================================================

export function CategoryFormDrawer({
  isOpen,
  onClose,
  category,
  onSubmit,
  isSaving,
}: CategoryFormDrawerProps) {
  const { t } = useTranslation()
  const isEdit = Boolean(category)

  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } =
    useForm<CategoryFormData>({
      resolver: zodResolver(categoryFormSchema),
      defaultValues: categoryToFormData(null),
    })

  useEffect(() => {
    if (isOpen) {
      reset(categoryToFormData(category))
    }
  }, [isOpen, category, reset])

  const isActive = watch('isActive')
  const colorValue = watch('color')

  const handleOpenChange = (open: boolean) => {
    if (!open) onClose()
  }

  function onFormSubmit(data: CategoryFormData) {
    onSubmit(buildSubmitPayload(data, category))
  }

  const headerContent = (
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/20">
        <Tag className="h-5 w-5 text-white" />
      </div>
      <div>
        <h2 className="text-lg font-semibold text-white">
          {isEdit ? t('products.editCategory') : t('products.addCategory')}
        </h2>
        <p className="text-sm text-white/70">
          {isEdit ? category?.name.en : t('products.subtitle')}
        </p>
      </div>
    </div>
  )

  const footerContent = (
    <div className="flex items-center justify-end gap-3">
      <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
        {t('common.cancel')}
      </Button>
      <Button type="submit" form="category-form" disabled={isSaving}>
        <Save className="h-4 w-4 me-2" />
        {isSaving ? t('common.saving') : t('common.save')}
      </Button>
    </div>
  )

  return (
    <BaseDrawer
      open={isOpen}
      onOpenChange={handleOpenChange}
      headerGradient="from-primary to-primary/80"
      headerContent={headerContent}
      footerContent={footerContent}
      fixedWidth={480}
      modal={false}
      title={isEdit ? t('products.editCategory') : t('products.addCategory')}
    >
      <form id="category-form" onSubmit={handleSubmit(onFormSubmit)} className="flex flex-col gap-5">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="cat-name-en">{t('products.form.nameEn')} *</Label>
          <Input
            id="cat-name-en"
            {...register('name.en')}
            placeholder={t('products.form.nameEn')}
          />
          {errors.name?.en && (
            <span className="text-xs text-destructive">{errors.name.en.message}</span>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="cat-name-ar">{t('products.form.nameAr')}</Label>
          <Input
            id="cat-name-ar"
            {...register('name.ar')}
            placeholder={t('products.form.nameAr')}
            dir="rtl"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="cat-code">{t('products.form.code')}</Label>
          <Input
            id="cat-code"
            {...register('code')}
            placeholder={t('products.form.codePlaceholder')}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="cat-color">{t('products.form.color')}</Label>
          <div className="flex items-center gap-3">
            <Input
              id="cat-color"
              {...register('color')}
              placeholder="#e2e8f0"
              className="flex-1"
            />
            <input
              type="color"
              value={colorValue || '#e2e8f0'}
              onChange={(e) => setValue('color', e.target.value)}
              className="h-9 w-9 cursor-pointer rounded-md border border-input p-0.5"
              aria-label={t('products.form.color')}
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="cat-desc-en">{t('products.form.descriptionEn')}</Label>
          <Input
            id="cat-desc-en"
            {...register('description.en')}
            placeholder={t('products.form.descriptionEn')}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="cat-desc-ar">{t('products.form.descriptionAr')}</Label>
          <Input
            id="cat-desc-ar"
            {...register('description.ar')}
            placeholder={t('products.form.descriptionAr')}
            dir="rtl"
          />
        </div>

        <div className="flex items-center justify-between rounded-lg border border-border p-3">
          <Label htmlFor="cat-isActive" className="cursor-pointer">
            {t('products.form.isActive')}
          </Label>
          <Switch
            id="cat-isActive"
            checked={isActive}
            onCheckedChange={(v) => setValue('isActive', v)}
          />
        </div>
      </form>
    </BaseDrawer>
  )
}
